import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Avatar,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TableSortLabel,
} from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import dayjs from "dayjs";
import { Transaction } from "shared/entities";

interface TransactionHistoryProps {
  transactions: Transaction[];
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  transactions,
}) => {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("30");
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };

  const handleDateFilterChange = (event: SelectChangeEvent<string>) => {
    setDateFilter(event.target.value);
  };

  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format("MMM D, YYYY");
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "completed":
        return {
          label: "Completed",
          color: "#66BB6A",
          bgColor: "#E8F5E9",
        };
      case "pending":
        return {
          label: "Pending",
          color: "#FFA726",
          bgColor: "#FFF3E0",
        };
      case "processing":
        return {
          label: "Processing",
          color: "#42A5F5",
          bgColor: "#E3F2FD",
        };
      default:
        return {
          label: "Unknown",
          color: "#9E9E9E",
          bgColor: "#F5F5F5",
        };
    }
  };

  // Filter transactions based on selected filters
  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      // Status filter
      if (statusFilter !== "all" && transaction.status !== statusFilter) {
        return false;
      }

      // Date filter (simplified - in production would use actual date comparison)
      // For now, just showing all transactions
      return true;
    });
  }, [transactions, statusFilter, dateFilter]);

  // Define columns
  const columns = useMemo<ColumnDef<Transaction>[]>(
    () => [
      {
        accessorKey: "clientName",
        header: "Client & Service",
        cell: ({ row }) => {
          const transaction = row.original;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: "#178B8E",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                }}
              >
                {transaction.clientInitials}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {transaction.clientName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {transaction.petName} - {transaction.serviceType} (
                  {transaction.duration})
                </Typography>
              </Box>
            </Box>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ getValue }) => {
          return (
            <Typography variant="body2">
              {formatDate(getValue() as string)}
            </Typography>
          );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const dateA = new Date(rowA.original.date).getTime();
          const dateB = new Date(rowB.original.date).getTime();
          return dateA - dateB;
        },
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ getValue }) => {
          return (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "#66BB6A" }}
            >
              ${(getValue() as number).toFixed(2)}
            </Typography>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue() as string;
          const statusConfig = getStatusConfig(status);
          return (
            <Chip
              label={statusConfig.label}
              size="small"
              sx={{
                backgroundColor: statusConfig.bgColor,
                color: statusConfig.color,
                fontWeight: 600,
                border: `1px solid ${statusConfig.color}`,
              }}
            />
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: "payoutDate",
        header: "Payout",
        cell: ({ row }) => {
          const transaction = row.original;
          return (
            <Typography variant="body2" color="text.secondary">
              {transaction.status === "pending"
                ? "After completion"
                : formatDate(transaction.payoutDate)}
            </Typography>
          );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          // Handle "After completion" by treating it as a far future date
          const getDateValue = (transaction: Transaction) => {
            if (transaction.status === "pending") {
              return new Date(2099, 11, 31).getTime();
            }
            return new Date(transaction.payoutDate).getTime();
          };

          return getDateValue(rowA.original) - getDateValue(rowB.original);
        },
      },
    ],
    [],
  );

  // Create table instance
  const table = useReactTable({
    data: filteredTransactions,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Transaction History
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              displayEmpty
              sx={{
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }}
            >
              <MenuItem value="all">All Transactions</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
            </Select>
          </FormControl>

          {/* Date Range Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={dateFilter}
              onChange={handleDateFilterChange}
              sx={{
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }}
            >
              <MenuItem value="30">Last 30 Days</MenuItem>
              <MenuItem value="90">Last 90 Days</MenuItem>
              <MenuItem value="180">Last 6 Months</MenuItem>
              <MenuItem value="365">Last Year</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id}>
                    {header.isPlaceholder ? null : (
                      <TableSortLabel
                        active={header.column.getIsSorted() !== false}
                        direction={
                          header.column.getIsSorted() === "asc" ? "asc" : "desc"
                        }
                        onClick={header.column.getToggleSortingHandler()}
                        sx={{
                          "&:hover": {
                            color: "primary.main",
                          },
                          "&.Mui-active": {
                            color: "primary.main",
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Typography>
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(23, 139, 142, 0.04)",
                    },
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    No transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {table.getPageCount() > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            pt: 2,
            borderTop: "1px solid #E0E0E0",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()} ({filteredTransactions.length} total
            transactions)
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              size="small"
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "rgba(23, 139, 142, 0.08)",
                },
              }}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>

            <Typography variant="body2" sx={{ px: 2 }}>
              {table.getState().pagination.pageIndex + 1}
            </Typography>

            <IconButton
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              size="small"
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "rgba(23, 139, 142, 0.08)",
                },
              }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default TransactionHistory;
